// streamSession.ts — open an EventSource without putting a credential in the URL.
//
// Three screens used to build stream URLs like `?token=<session JWT>`. A URL is
// the one part of a request that infrastructure copies into permanent storage by
// default — it was verified on the production host that every logged event-stream
// line carried the token, in an unrotated container log.
//
// The browser's EventSource cannot set an Authorization header, which is exactly
// why the token ended up in the query string. The seam:
//
//   1. POST /api/events/session with the normal Authorization header — an
//      ordinary request whose credential travels in a header, not a URL.
//   2. The server replies with an HttpOnly, Secure, SameSite=Strict cookie.
//   3. EventSource is opened with a CLEAN url. The browser attaches the cookie
//      itself, so the stream and its built-in reconnect work unchanged.
//
// Reconnect behaviour is deliberately preserved. EventSource's own backoff is
// left alone; this module only adds a single re-exchange when the cookie has
// lapsed, so a dropped stream recovers without the user noticing and without a
// reconnect storm.

const TOKEN_KEY = "kontractai_token";

/** Ask the server for a stream cookie. Returns false if we are not signed in. */
export async function ensureStreamSession(): Promise<boolean> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return false;
  try {
    const r = await fetch("/api/events/session", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      // Same-origin, but stated explicitly: the response SETS a cookie and the
      // browser must be told to keep it.
      credentials: "same-origin",
    });
    return r.ok;
  } catch {
    return false;
  }
}

/** Drop the stream cookie. Call on sign-out so it does not outlive the session. */
export async function endStreamSession(): Promise<void> {
  try {
    await fetch("/api/events/session", { method: "DELETE", credentials: "same-origin" });
  } catch {
    /* best effort — the cookie is short-lived and HttpOnly either way */
  }
}

export interface StreamHandle {
  /** The live EventSource, or null before the first successful exchange. */
  source: () => EventSource | null;
  close: () => void;
}

/**
 * Open an authenticated EventSource at `url` (which must carry NO credential).
 *
 * `wire` attaches listeners and is re-run on every reconnect, because a new
 * EventSource object needs its listeners attached again.
 */
export function openAuthenticatedStream(
  url: string,
  wire: (es: EventSource) => void,
  opts: { onError?: () => void } = {},
): StreamHandle {
  let es: EventSource | null = null;
  let closed = false;
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const connect = async () => {
    if (closed) return;
    // Re-exchange before every attempt. The cookie is short-lived by design, so
    // a reconnect after an idle period must refresh it or the stream would 401
    // forever — the failure mode a naive "just reconnect" loop would produce.
    const ok = await ensureStreamSession();
    if (closed) return;
    if (!ok) {
      // Not signed in, or the exchange failed. Back off rather than hammering
      // an endpoint that is telling us no.
      schedule();
      return;
    }
    es = new EventSource(url, { withCredentials: true });
    wire(es);
    es.addEventListener("open", () => { attempts = 0; });
    es.onerror = () => {
      if (es) { es.close(); es = null; }
      if (opts.onError) opts.onError();
      schedule();
    };
  };

  const schedule = () => {
    if (closed) return;
    attempts += 1;
    // Same shape as the backoff this replaced: 2s, 4s, 8s, 16s, capped at 30s.
    const delay = Math.min(2000 * Math.pow(2, attempts - 1), 30000);
    timer = setTimeout(connect, delay);
  };

  void connect();

  return {
    source: () => es,
    close: () => {
      closed = true;
      if (timer) clearTimeout(timer);
      if (es) { es.close(); es = null; }
    },
  };
}
